import { supabase } from '../lib/supabase';
import { 
  WholesaleRequest, 
  WholesaleRequestRow, 
  WholesaleRequestInsert,
  WholesaleRequestUpdate,
  WholesaleRequestStatus 
} from '../types/wholesale.types';

interface WholesaleRequestResponse {
  data: WholesaleRequest[] | null;
  error: Error | null;
}

interface SingleWholesaleRequestResponse {
  data: WholesaleRequest | null;
  error: Error | null;
}

// Convert database row (snake_case) to app type (camelCase)
function convertToAppType(row: WholesaleRequestRow): WholesaleRequest {
  return {
    id: row.id,
    companyName: row.company_name,
    contactPerson: row.contact_person,
    email: row.email,
    phone: row.phone,
    businessType: row.business_type,
    productType: row.product_type || undefined,
    productInterest: row.product_interest,
    estimatedQuantity: row.estimated_quantity,
    deliveryFrequency: row.delivery_frequency,
    preferredCurrency: row.preferred_currency || undefined,
    message: row.message || undefined,
    requestStatus: row.request_status as WholesaleRequestStatus,
    adminNotes: row.admin_notes || undefined,
    assignedTo: row.assigned_to || undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    completedAt: row.completed_at || undefined,
  };
}

export const wholesaleService = {
  // Create new wholesale request (public access)
  async create(formData: Omit<WholesaleRequest, 'id' | 'createdAt' | 'updatedAt' | 'requestStatus' | 'adminNotes' | 'assignedTo' | 'completedAt'>): Promise<SingleWholesaleRequestResponse> {
    try {
      const insertData: WholesaleRequestInsert = {
        company_name: formData.companyName,
        contact_person: formData.contactPerson,
        email: formData.email,
        phone: formData.phone,
        business_type: formData.businessType,
        product_type: formData.productType,
        product_interest: formData.productInterest,
        estimated_quantity: formData.estimatedQuantity,
        delivery_frequency: formData.deliveryFrequency,
        preferred_currency: formData.preferredCurrency,
        message: formData.message,
      };

      const { data, error } = await supabase
        .from('wholesale_requests')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      return { data: convertToAppType(data), error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Get all requests (admin only)
  async getAll(): Promise<WholesaleRequestResponse> {
    try {
      const { data, error } = await supabase
        .from('wholesale_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const requests = data?.map(convertToAppType) || [];
      return { data: requests, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Get requests by status (admin only)
  async getByStatus(status: WholesaleRequestStatus): Promise<WholesaleRequestResponse> {
    try {
      const { data, error } = await supabase
        .from('wholesale_requests')
        .select('*')
        .eq('request_status', status)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const requests = data?.map(convertToAppType) || [];
      return { data: requests, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Update request (admin only)
  async update(id: string, updates: WholesaleRequestUpdate): Promise<SingleWholesaleRequestResponse> {
    try {
      const updateData: Partial<WholesaleRequestRow> = {};
      
      if (updates.requestStatus) updateData.request_status = updates.requestStatus;
      if (updates.adminNotes !== undefined) updateData.admin_notes = updates.adminNotes;
      if (updates.assignedTo !== undefined) updateData.assigned_to = updates.assignedTo;

      const { data, error } = await supabase
        .from('wholesale_requests')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return { data: convertToAppType(data), error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Get statistics (admin only)
  async getStats(): Promise<{ data: Record<WholesaleRequestStatus, number> | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('wholesale_requests')
        .select('request_status');

      if (error) throw error;

      const stats: Record<WholesaleRequestStatus, number> = {
        new: 0,
        in_progress: 0,
        quoted: 0,
        completed: 0,
        rejected: 0,
      };

      data?.forEach((row) => {
        if (row?.request_status) {
          stats[row.request_status]++;
        }
      });

      return { data: stats, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },
};